import React, { useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import ProfessionalProfile from 'pages/NewUniProfile/components/ProfessionalProfileStep/components/ProfessionalProfile';
import AdditionalMedia from 'pages/NewUniProfile/components/ProfessionalProfileStep/components/AdditionalMedia';
import Healthcare from 'pages/NewUniProfile/components/BackgroundStep/components/Healthcare';
import Education from 'pages/NewUniProfile/components/BackgroundStep/components/Education';
import Treatment from 'pages/NewUniProfile/components/BackgroundStep/components/Treatment';
import DemographicDetails from 'pages/NewUniProfile/components/BackgroundStep/components/DemographicDetails';
import Recognition from 'pages/NewUniProfile/components/BackgroundStep/components/Recognition';
import MalpracticeInsuranceCertificate from 'pages/NewUniProfile/components/LicenseStep/components/MalpracticeInsuranceCertificate';
import DEALicense from 'pages/NewUniProfile/components/LicenseStep/components/DeaLicense';
import CollaboratingPhysician from 'pages/NewUniProfile/components/LicenseStep/components/CollaboratingPhysician';
import AdditionalLicense from 'pages/NewUniProfile/components/LicenseStep/components/AdditionalLicense';
import BasicInfo from 'pages/PPAAdvanceSettingPage/components/ProfileEnrich/components/BasicInfo';
import { useBasicInfo } from 'hooks/newUniprofile/useBasicInfo';
import { useLicenseInfo } from 'hooks/newUniprofile/useLicenseInfo';
import { useProfessionalProfile } from 'hooks/newUniprofile/useProfessionalProfile';
import { useBackgroundInfo } from 'hooks/newUniprofile/useBackgroundInfo';
import { useSwitchedProvider } from 'pages/ReviewProviderProfilePage/SwitchedProviderContext';
import s from './s.module.less';

export type EnrichProfileStepRef = { submit: () => Promise<boolean> };

const EnrichProfileStep = forwardRef<EnrichProfileStepRef>((_props, ref) => {
    const { scheduleToken } = useSwitchedProvider();
    const [basicInfo, , fetchBasicInfoData] = useBasicInfo(false, scheduleToken);
    const [licenseInfo, , fetchLicenseData] = useLicenseInfo(false, scheduleToken);
    const [professionalProfile, , fetchProfessionalProfileData] = useProfessionalProfile(false, scheduleToken);
    const [background, , , updateBackgroundInfo] = useBackgroundInfo(false, scheduleToken);
    const basicInfoRef = useRef<any>(null);
    const professionalProfileRef = useRef<any>(null);

    // Persist basic info and the professional profile to the created provider before sending the
    // invitation; either onSubmit throws on validation failure so the caller can abort.
    useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                // Validate both concurrently first (no side effects), so a failure in either aborts
                // before any save fires and avoids a partial save.
                await Promise.all([basicInfoRef.current?.onValidate(), professionalProfileRef.current?.onValidate()]);
                await basicInfoRef.current?.onSubmit();
                await professionalProfileRef.current?.onSubmit();
                return true;
            } catch {
                return false;
            }
        },
    }));

    const hasAdditionalMedia = Boolean(
        professionalProfile?.additionalPhotoList?.length ||
        professionalProfile?.videoList?.length ||
        professionalProfile?.foreignLanguage,
    );

    const hasAdditionalLicense =
        !!licenseInfo?.additionalLicensesNCertificates?.controlledSubstanceCertification?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.continuingMedicalEducationCertificates?.length ||
        !!licenseInfo?.additionalLicensesNCertificates?.autonomy?.url ||
        !!licenseInfo?.additionalLicensesNCertificates?.other?.url;

    const hasContentForBiographic = useMemo(() => {
        return Object.values(background.biographic).some((value) => Boolean(value));
    }, [background]);

    const hasContentForRecognition = useMemo(() => {
        return (
            Boolean(background.awardRecognitionList?.length) ||
            Boolean(background.associationRecognitionList?.length) ||
            Boolean(background.publicationRecognitionList?.length)
        );
    }, [background]);

    return (
        <div className={s.wrap}>
            <h1 className={s.heading}>Enrich marketing profile</h1>
            <p className={s.description}>
                Complete the provider&apos;s information to highlight their expertise and attract more patients with
                confidence.
            </p>

            <BasicInfo
                initValue={basicInfo}
                refetch={fetchBasicInfoData}
                ref={basicInfoRef}
                scheduleToken={scheduleToken}
            />

            <FormCard type="edit" ref={professionalProfileRef}>
                {(isEditing) => (
                    <ProfessionalProfile
                        isEditing={isEditing}
                        initValue={professionalProfile}
                        refetch={fetchProfessionalProfileData}
                        showTitle
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasAdditionalMedia ? 'review' : 'add'}
                title="Additional Media"
                isOptional
                hasContent={hasAdditionalMedia}
            >
                {(isEditing) => (
                    <AdditionalMedia
                        isEditing={isEditing}
                        initValue={professionalProfile}
                        refetch={fetchProfessionalProfileData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={Boolean(background.providerExperienceList?.length) ? 'review' : 'add'}
                title="Healthcare experience"
                isOptional
                hasContent={Boolean(background.providerExperienceList?.length)}
            >
                {(isEditing) => (
                    <Healthcare
                        isEditing={isEditing}
                        initValue={background.providerExperienceList}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={Boolean(background.providerEducationList?.length) ? 'review' : 'add'}
                title="Education"
                isOptional
                hasContent={Boolean(background.providerEducationList?.length)}
            >
                {(isEditing) => (
                    <Education
                        isEditing={isEditing}
                        initValue={background.providerEducationList}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={Boolean(background.treatmentPhilosophy) ? 'review' : 'add'}
                title="Treatment methodology"
                isOptional
                hasContent={Boolean(background.treatmentPhilosophy)}
            >
                {(isEditing) => (
                    <Treatment
                        isEditing={isEditing}
                        initValue={background.treatmentPhilosophy}
                        conversationId={background.philosophyConversationId}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasContentForBiographic ? 'review' : 'add'}
                title="Demographic details"
                isOptional
                hasContent={hasContentForBiographic}
            >
                {(isEditing) => (
                    <DemographicDetails
                        isEditing={isEditing}
                        initValue={background.biographic}
                        hasContent={hasContentForBiographic}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasContentForRecognition ? 'review' : 'add'}
                title="Recognition"
                isOptional
                hasContent={hasContentForRecognition}
            >
                {(isEditing) => (
                    <Recognition
                        isEditing={isEditing}
                        awardRecognitionList={background.awardRecognitionList}
                        associationRecognitionList={background.associationRecognitionList}
                        publicationRecognitionList={background.publicationRecognitionList}
                        updateFn={updateBackgroundInfo}
                    />
                )}
            </FormCard>

            <FormCard
                type={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber ? 'review' : 'add'}
                title="Malpractice insurance certificate"
                isOptional
                hasContent={!!licenseInfo?.malpracticeInsuranceCertificate?.licenseNumber}
            >
                {(isEditing) => (
                    <MalpracticeInsuranceCertificate
                        isEditing={isEditing}
                        initValue={licenseInfo?.malpracticeInsuranceCertificate}
                        refetch={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={!!licenseInfo?.deaLicense?.length ? 'review' : 'add'}
                title="DEA licenses"
                isOptional
                hasContent={!!licenseInfo?.deaLicense?.length}
            >
                {(isEditing) => (
                    <DEALicense
                        isEditing={isEditing}
                        initValue={licenseInfo?.deaLicense}
                        refetch={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={!!licenseInfo?.collaboratingPhysician?.length ? 'review' : 'add'}
                title="Collaborating physician"
                isOptional
                hasContent={!!licenseInfo?.collaboratingPhysician?.length}
            >
                {(isEditing) => (
                    <CollaboratingPhysician
                        isEditing={isEditing}
                        initValue={licenseInfo?.collaboratingPhysician}
                        refresh={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>

            <FormCard
                type={hasAdditionalLicense ? 'review' : 'add'}
                title="Additional licences and certificates"
                isOptional
                hasContent={hasAdditionalLicense}
            >
                {(isEditing) => (
                    <AdditionalLicense
                        isEditing={isEditing}
                        initValue={licenseInfo?.additionalLicensesNCertificates}
                        refetch={fetchLicenseData}
                        scheduleToken={scheduleToken}
                    />
                )}
            </FormCard>
        </div>
    );
});

export default EnrichProfileStep;
